import type { CellContext } from '@tanstack/react-table';
import { nanoid } from 'nanoid';
import * as React from 'react';
import type { FormErrorsWithArray, WrappedFieldArrayProps } from 'redux-form';
import { Field } from 'redux-form';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { dollarFormatter } from 'af-utils/format.util';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import DateInput from 'af-fields/DateInput';
import Input from 'af-fields/Input';
import type { SimpleTableRow } from 'af-fields/SimpleTable';
import SimpleTable from 'af-fields/SimpleTable';

import type { FooterButton } from 'af-components/Controls/SimpleTable/types';

import type { InvoiceFM } from '../formModel';

export type InstallmentFM = Required<InvoiceFM>['installments'][0];

export interface OwnProps {
	setUnsavedChanges: (value: boolean) => void;
	invoiceId: number;
	errors: FormErrorsWithArray<InvoiceFM, string>;
	initialized: boolean;
	updateTotalPaid: (installments: InstallmentFM[]) => void;
}

type Props = OwnProps & WrappedFieldArrayProps<InstallmentFM>;

const InstallmentItems: React.FC<Props> = (props) => {
	const { fields, errors, updateTotalPaid, invoiceId, setUnsavedChanges, initialized } = props;

	const addItem = React.useCallback(() => {
		fields.push({
			id: nanoid(UNIQUE_ID_SIZE),
			invoiceId: invoiceId,
			instNumber: fields.length,
			amount: null,
			datePaid: null,
			note: null,
		});
		setUnsavedChanges(true);
	}, [fields, invoiceId, setUnsavedChanges]);

	const footerButtons = React.useMemo(() => {
		const resolvedFooterButtons = [{
			iconName: 'ActionPlusSmall',
			label: 'Add Installment',
			onClick: addItem,
		}];
		return resolvedFooterButtons as FooterButton[];
	}, [addItem]);

	const columns = React.useMemo(() => [{
		id: 'instNumber',
		cell: (_cell: CellContext<InstallmentFM & SimpleTableRow, number>) => {
			return _cell.row.original.index + 1 ?? 'N/A';
		},
		header: 'No.',
		accessor: 'instNumber',
		enableSorting: true,
	},
	{
		id: 'amount',
		cell: (_cell: CellContext<InstallmentFM & SimpleTableRow, number>) => {
			if (_cell.row.original.isInEditMode) {
				return (
					<Field
						addonAfter="$"
						component={Input}
						isZeroIfEmpty={true}
						name={`${_cell.row.original.name}.amount`}
						type="number"
						withThousandsSeparator={true}
					/>
				);
			}
			return dollarFormatter.format(_cell.getValue() ?? 0);
		},
		header: 'Amount',
		accessor: 'amount',
		enableSorting: true,
	},
	{
		id: 'datePaid',
		cell: (_cell: CellContext<InstallmentFM & SimpleTableRow, DateInput>) => {
			if (_cell.row.original.isInEditMode) {
				return (
					<Field
						component={DateInput}
						dateFormat={TimeFormat.DB_DATE_ONLY}
						fixed={true}
						id="datePaid"
						name={`${_cell.row.original.name}.datePaid`}
						originalDateFormat={TimeFormat.DB_DATE_ONLY}
						placeholderText={TimeFormat.DB_DATE_ONLY}
						required={true}
					/>
				);
			}
			return _cell.getValue() ?? 'N/A';
		},
		header: 'Date Paid',
		accessor: 'datePaid',
		enableSorting: true,
	},
	{
		id: 'note',
		cell: (_cell: CellContext<InstallmentFM & SimpleTableRow, string>) => {
			if (_cell.row.original.isInEditMode) {
				return (
					<Field
						component={Input}
						name={`${_cell.row.original.name}.note`}
					/>
				);
			}
			return _cell.getValue() ?? 'N/A';
		},
		header: 'Note',
		accessor: 'note',
		enableSorting: true,
		size: 300,
	},
	], []);

	return (
		<SimpleTable
			allowEdit={true}
			columns={columns}
			emptyTableMessage='No installments added. To do that click "+ Add Installment".'
			errors={errors.installments}
			fields={fields}
			footerButtonsLeft={footerButtons}
			initialized={initialized}
			label="Installments"
			notifyAreThereItemsInEditMode={setUnsavedChanges}
			onFieldsUpdated={updateTotalPaid}
		/>
	);
};

export default React.memo(InstallmentItems);
